import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { marketingAPI, deletionAuditAPI } from '@/lib/api';
import DeleteRequestDialog from '@/components/marketing/DeleteRequestDialog';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatDate } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Upload, Download, XCircle, ChevronDown, ChevronRight, Image as ImageIcon, Building2, Trash2 } from 'lucide-react';

const STATUS_BADGE = {
  Submitted: { label: 'Submitted', cls: 'bg-amber-100 text-amber-700' },
  Ready: { label: 'Ready', cls: 'bg-emerald-100 text-emerald-700' },
  Declined: { label: 'Declined', cls: 'bg-rose-100 text-rose-700' },
};

function AttachmentThumb({ path, kind, filename, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    marketingAPI.attachmentBlob(path).then((res) => {
      const url = URL.createObjectURL(res.data);
      revoke = url;
      setSrc(url);
    }).catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  return (
    <button type="button" onClick={() => onClick?.(src)} className="group flex items-center gap-2 bg-white border rounded-md p-2 hover:border-slate-400 transition-colors w-full text-left">
      <div className="w-12 h-12 rounded bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
        {src ? <img src={src} alt="" className="max-w-full max-h-full object-contain" /> : <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">{filename || `${kind} file`}</p>
        <p className="text-[10px] text-slate-500 capitalize">{kind}</p>
      </div>
      {src && (
        <a href={src} download={filename || `${kind}.png`} onClick={(e) => e.stopPropagation()} className="text-slate-400 hover:text-slate-700">
          <Download size={14} />
        </a>
      )}
    </button>
  );
}

export default function MarketingRequestsPage() {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await marketingAPI.listRequests();
      setRequests(res.data.requests || []);
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUploadFinal = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await marketingAPI.uploadFinal(id, fd);
      toast.success('Final artwork uploaded — agent can now download');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingId(null); }
  };

  const handleDecline = async (id) => {
    const reason = window.prompt('Reason for decline?', '');
    if (reason === null) return;
    try {
      await marketingAPI.declineRequest(id, reason);
      toast.success('Order declined');
      load();
    } catch { toast.error('Decline failed'); }
  };

  const handleDelete = (req) => setDeleteTarget(req);

  const performDelete = async ({ reason, reasonPreset }) => {
    if (!deleteTarget) return;
    const req = deleteTarget;
    try {
      const r = await marketingAPI.deleteRequest(req.request_id, {
        reason, reason_preset: reasonPreset,
      });
      const auditId = r.data?.audit_id;
      toast.success('Request deleted', {
        duration: 10_000,
        action: auditId ? {
          label: 'Undo',
          onClick: async () => {
            try {
              await deletionAuditAPI.restore(auditId);
              toast.success('Request restored');
              load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Restore failed');
            }
          },
        } : undefined,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
      throw e;
    }
  };

  const handleDownload = async (req) => {
    try {
      const blob = await marketingAPI.downloadFile(req.request_id);
      const url = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = req.final_ext || 'pdf';
      a.download = `${(req.template_title || 'marketing').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div data-testid="marketing-requests-page">
      <PageHeader
        title={isAdmin ? 'Marketing Orders' : 'My Marketing Orders'}
        description={isAdmin ? 'Review form submissions, design the piece, and upload the final artwork.' : 'Track your marketing orders and download the finished artwork.'}
        actions={<Link to="/marketing"><Button variant="outline"><ArrowLeft size={16} className="mr-2" />Back to Catalog</Button></Link>}
      />
      <div className="page-content">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : requests.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-500">No orders yet.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => {
              const s = STATUS_BADGE[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-700' };
              const isOpen = !!expanded[r.request_id];
              return (
                <Card key={r.request_id} data-testid={`request-card-${r.request_id}`}>
                  <CardContent className="p-4">
                    <button
                      type="button"
                      onClick={() => setExpanded({ ...expanded, [r.request_id]: !isOpen })}
                      className="w-full flex items-center justify-between gap-3 text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {isOpen ? <ChevronDown size={16} className="text-slate-400 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900">{r.template_title}</span>
                            <Badge className={s.cls}>{s.label}</Badge>
                            {r.print_order?.enabled && <Badge variant="outline" className="text-[10px]">Print +{r.print_order.quantity}</Badge>}
                          </div>
                          {isAdmin && <p className="text-xs text-slate-500 mt-0.5">{r.requester_name} • {r.requester_email}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{formatDate(r.created_at)}</span>
                    </button>

                    {isOpen && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        {/* Field values */}
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Form Submission</p>
                          {Object.keys(r.field_values || {}).length === 0 ? (
                            <p className="text-xs text-slate-500">No form fields submitted.</p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {Object.entries(r.field_values).map(([k, v]) => (
                                <div key={k} className="bg-slate-50 rounded p-2.5 text-xs">
                                  <div className="text-[10px] uppercase text-slate-400">{k.replace(/_/g, ' ')}</div>
                                  <div className="text-slate-800 whitespace-pre-wrap break-words mt-0.5">{v || <span className="text-slate-400">—</span>}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {r.notes && (
                            <div className="bg-amber-50 rounded p-2.5 text-xs mt-2">
                              <div className="text-[10px] uppercase text-amber-600">Notes from agent</div>
                              <div className="text-slate-800 whitespace-pre-wrap break-words mt-0.5">{r.notes}</div>
                            </div>
                          )}
                        </div>

                        {/* Agency brand */}
                        {r.agency_brand?.logo_path && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                              <Building2 size={11} />Agency Brand{r.agency_brand.inherited_from_upline ? ' (inherited from upline)' : ''}
                            </p>
                            <AttachmentThumb
                              path={r.agency_brand.logo_path}
                              kind={r.agency_brand.agency_name || 'agency_logo'}
                              filename={`${(r.agency_brand.agency_name || 'agency').replace(/\s+/g, '_')}_logo`}
                            />
                          </div>
                        )}

                        {/* Attachments */}
                        {(r.attachments || []).length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
                              <ImageIcon size={11} />Attachments ({r.attachments.length})
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {r.attachments.map((a) => (
                                <AttachmentThumb
                                  key={a.storage_path}
                                  path={a.storage_path}
                                  kind={a.kind}
                                  filename={a.filename}
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Print order summary — legacy block. New storefront
                            orders use the shop cart; this only renders for
                            historical marketing_requests migrated from the
                            retired Print Order flow. */}
                        {r.print_order?.enabled && (
                          <div className="bg-sky-50 rounded p-3 text-xs">
                            <p className="text-[10px] uppercase text-sky-700 mb-1.5">Print order (legacy)</p>
                            <p className="text-slate-700">
                              Quantity: <strong>{r.print_order.quantity?.toLocaleString()} units</strong> &middot;
                              Status: <strong>{r.print_order.status}</strong>
                              {r.print_order.quoted_price != null && <> &middot; ${r.print_order.quoted_price.toFixed(2)}</>}
                            </p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 flex-wrap pt-2">
                          {r.final_path && (
                            <Button size="sm" variant="outline" onClick={() => handleDownload(r)} data-testid={`download-btn-${r.request_id}`}>
                              <Download size={14} className="mr-1.5" />Download Final
                            </Button>
                          )}
                          {isAdmin && r.status !== 'Declined' && (
                            <>
                              <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                className="hidden"
                                ref={(el) => { fileInputs.current[r.request_id] = el; }}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handleUploadFinal(r.request_id, f);
                                  e.target.value = '';
                                }}
                              />
                              <Button
                                size="sm"
                                onClick={() => fileInputs.current[r.request_id]?.click()}
                                disabled={uploadingId === r.request_id}
                                data-testid={`upload-final-btn-${r.request_id}`}
                              >
                                {uploadingId === r.request_id ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
                                {r.final_path ? 'Replace Final' : 'Upload Final Artwork'}
                              </Button>
                              {r.status !== 'Declined' && (
                                <Button size="sm" variant="outline" onClick={() => handleDecline(r.request_id)}>
                                  <XCircle size={14} className="mr-1.5 text-rose-500" />Decline
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(r)}
                                data-testid={`delete-request-btn-${r.request_id}`}
                                className="border-rose-300 hover:bg-rose-50 text-rose-700"
                              >
                                <Trash2 size={14} className="mr-1.5" />Delete
                              </Button>
                            </>
                          )}
                        </div>

                        {r.decline_reason && (
                          <p className="text-xs text-rose-600 mt-1">Decline reason: {r.decline_reason}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <DeleteRequestDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        resourceLabel="marketing request"
        summary={deleteTarget ? `${deleteTarget.template_title || 'Marketing request'} — ${deleteTarget.requester_name || ''}` : ''}
        onConfirm={performDelete}
      />
    </div>
  );
}
